import {
  AppBase,
  baseFromDoc,
  FirestoreBaseDoc,
  makeDocConverter,
} from '../base/firestore-model-base'
import type { Timestamp } from 'firebase/firestore'
import { toDate } from '../../utils/date-util'

/**
 * Contact（お問い合わせ）
 *
 * - `ContactDoc`: Firestore に保存される形（Timestamp のまま / snake_case）
 * - `ContactEntity`: アプリで使う形（Date に変換 / snake_case）※ CRUD と UI 共通
 *
 * 保存先は `school_accounts/{schoolAccountId}/contacts/{contactId}` を前提にする。
 */

export type ContactStatus =
  | 'in_progress'
  | 'irogami_answered'
  | 'waiting_admin'
  | 'completed'

export type ContactMessageDoc = {
  sender: 'school' | 'admin'
  sender_name: string
  content: string
  created_at: Timestamp
}

/** アプリで使う形（snake_case） */
export type ContactMessageEntity = {
  sender: 'school' | 'admin'
  sender_name: string
  content: string
  created_at: Date
}

export type ContactDoc = FirestoreBaseDoc & {
  subject: string
  message: string
  status: ContactStatus

  project_id?: string | null
  project_path?: string | null
  project_number?: string | null

  school_account?: string | null
  closed_by_admin_account?: string | null

  messages?: ContactMessageDoc[] | null

  response_deadline?: Timestamp | null
}

/** アプリで使う形（object / Date に変換・snake_case）※ UI 含め共通で利用 */
export type ContactEntity = AppBase & {
  subject: string
  message: string
  status: ContactStatus

  project_id?: string | null
  project_path?: string | null
  project_number?: string | null

  school_account?: string | null
  /** UI用: 学校アカウントID（school_account パスから抽出） */
  school_account_id?: string | null

  closed_by_admin_account?: string | null

  messages: ContactMessageEntity[]

  response_deadline?: Date | null
}

export const contactDocConverter = makeDocConverter<ContactDoc>()

function toContactMessageEntity(doc: ContactMessageDoc): ContactMessageEntity {
  return {
    sender: doc.sender,
    sender_name: doc.sender_name,
    content: doc.content,
    created_at: toDate(doc.created_at),
  }
}

export function toContactEntity(id: string, docData: ContactDoc, path: string): ContactEntity {
  /** 既存データ互換: DocumentReference または string から path を抽出 */
  const refToPath = (v: unknown): string | null =>
    typeof v === 'string' ? v : (v as { path?: string; id?: string } | null)?.path ?? (v as { id?: string } | null)?.id ?? null

  const school_account = refToPath((docData as { school_account?: unknown }).school_account)
  const school_account_id = school_account?.split('/').pop() ?? undefined

  const rawProjectId = (docData as { project_id?: unknown }).project_id
  const project_id = typeof rawProjectId === 'string' ? rawProjectId : refToPath(rawProjectId)

  const base = baseFromDoc(id, docData, path)

  return {
    ...base,
    subject: docData.subject,
    message: docData.message,
    status: docData.status,
    project_id,
    project_path: docData.project_path ?? null,
    project_number: docData.project_number ?? null,
    school_account,
    school_account_id: school_account_id ?? undefined,
    closed_by_admin_account: docData.closed_by_admin_account ?? null,
    messages: (docData.messages ?? []).map(toContactMessageEntity),
    response_deadline: docData.response_deadline ? toDate(docData.response_deadline) : null,
  }
}


